// indexedDBService.ts
const DB_NAME = "AppDatabase";
const DB_VERSION = 1;

function toDbError(event, fallbackMessage = "IndexedDB operation failed") {
  const domError = event?.target?.error;
  const error = new Error(domError?.message || fallbackMessage);
  if (domError?.name) {
    error.name = domError.name;
  }
  return error;
}

export function openDB(storeNames) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      storeNames.forEach((storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id", autoIncrement: true });
        }
      });
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(toDbError(event, "Failed to open IndexedDB"));
  });
}

export async function addItem(storeName, item) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.add(item);

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(toDbError(e, `Failed to add item to ${storeName}`));
  });
}

export async function updateItem(storeName, item) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.put(item);

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(toDbError(e, `Failed to update item in ${storeName}`));
  });
}

export async function getItem(storeName, id) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(toDbError(e, `Failed to get item from ${storeName}`));
  });
}

export async function getAll(storeName) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(toDbError(e, `Failed to get all items from ${storeName}`));
  });
}

export async function deleteItem(storeName, id) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(toDbError(e, `Failed to delete item from ${storeName}`));
  });
}

// Clear all records
export async function clearTable(storeName) {
  const db = await openDB([storeName]);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(true);
    request.onerror = (e) => reject(toDbError(e, `Failed to clear ${storeName}`));
  });
}